import { Theme, Tooltip } from '@mui/material';
import { path } from 'd3-path';
import { FC, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { ChartPoint, RawDataItem } from './funnel-chart-types';
import {
  createChartInfo,
  createFunnelChartOptions,
} from './funnel-chart-utils';
import TooltipContent from './TooltipContent';

const options = createFunnelChartOptions();

const FunnelChart: FC<FunnelChartProps> = ({ data, width, height }) => {
  const { classes, cx } = useStyles();
  const [hoverIndex, setHoverIndex] = useState(-1);
  const [tooltipPosition, setTooltipPosition] = useState<ChartPoint | null>(
    null,
  );

  const handleMouseEnter = (e: React.MouseEvent<SVGGElement>) => {
    const groupElement = e.currentTarget;
    const pathElement = groupElement.querySelector<SVGPathElement>('.link');
    const targetIndex = parseInt(
      groupElement.getAttribute('data-index') || '0',
    );

    if (pathElement) {
      setTooltipPosition(calculateCenter(pathElement));
    }
    setHoverIndex(targetIndex);
  };

  const handleMouseLeave = () => {
    setHoverIndex(-1);
    setTooltipPosition(null);
  };

  const info = useMemo(
    () => createChartInfo(options, data, width, height),
    [data, width, height],
  );

  return (
    <svg
      className={classes.svg}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='none'
      width={width + 'px'}
      height={height + 'px'}>
      {info.startNodes.map((startNode, index) => {
        const endNode = info.endNodes[index];
        const { points } = info.links[index];
        const p = path();
        p.moveTo(points[0].x, points[0].y);
        p.lineTo(points[1].x, points[1].y);
        p.lineTo(points[2].x, points[2].y);
        p.lineTo(points[3].x, points[3].y);

        return (
          <g
            key={startNode.name}
            className={cx(
              hoverIndex === index && classes.hover,
              hoverIndex > -1 && hoverIndex !== index && classes.notHover,
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-index={index}>
            {index === hoverIndex && tooltipPosition && (
              <foreignObject
                x={tooltipPosition.x}
                y={tooltipPosition.y}
                width='1px'
                height='1px'>
                <Tooltip
                  title={
                    <TooltipContent
                      severityName={startNode.name}
                      start={startNode.value}
                      end={endNode.value}
                      color={startNode.color}
                    />
                  }
                  arrow
                  placement='top'
                  open>
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Tooltip>
              </foreignObject>
            )}

            <rect
              x={startNode.x}
              y={startNode.y}
              width={startNode.width}
              height={startNode.height}
              fill={startNode.color}
              strokeWidth={startNode.strokeWidth}
            />

            <path
              d={p.toString()}
              fill={startNode.color}
              strokeWidth={startNode.strokeWidth}
              className='link'
            />

            <rect
              key={endNode.name}
              width={endNode.width}
              height={endNode.height}
              x={endNode.x}
              y={endNode.y}
              fill={endNode.color}
              strokeWidth={endNode.strokeWidth}
            />
          </g>
        );
      })}
    </svg>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  svg: {
    backgroundColor: theme.palette.background.paper,
    '*': {
      transition: theme.transitions.create('opacity', {
        duration: 500,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    'path, rect': {
      stroke: theme.palette.background.paper,
    },
  },
  hover: {
    '& > *': {
      opacity: 1,
    },
  },
  notHover: {
    '& > *:not(text)': {
      opacity: 0.3,
    },
  },
  textCenter: {
    textAnchor: 'middle',
    dominantBaseline: 'middle',
    fill: theme.palette.mode === 'dark' ? '#777' : theme.palette.text.primary,
    fontWeight: 'bold',
  },
}));

const calculateCenter = (path: SVGPathElement): ChartPoint => {
  const { x, y, width, height } = path.getBBox();
  return { x: x + width / 2, y: y + height / 2 };
};

export interface FunnelChartProps {
  data: RawDataItem[];
  width: number;
  height: number;
}
export default FunnelChart;
